import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Plus, Edit, Trash2, Users, AlertTriangle } from 'lucide-react'

export function DriversManager() {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editingDriver, setEditingDriver] = useState<any>(null)
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['drivers'],
    queryFn: async () => {
      const res = await fetch('/api/manage/drivers')
      return res.json() as Promise<{ drivers: any[] }>
    },
  })

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await fetch('/api/manage/drivers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
      if (!res.ok) throw new Error('Failed to create driver')
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drivers'] })
      setIsDialogOpen(false)
      setEditingDriver(null)
    },
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const res = await fetch(`/api/manage/drivers/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
      if (!res.ok) throw new Error('Failed to update driver')
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drivers'] })
      setIsDialogOpen(false)
      setEditingDriver(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/manage/drivers/${id}`, { method: 'DELETE' })
      if (!res.ok) throw new Error('Failed to delete driver')
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drivers'] })
    },
  })

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const data = {
      name: formData.get('name'),
      email: formData.get('email'),
      phone: formData.get('phone'),
      license_number: formData.get('license_number'),
      license_state: formData.get('license_state'),
      license_expires: formData.get('license_expires') ? new Date(formData.get('license_expires') as string).getTime() / 1000 : null,
      cdl_class: formData.get('cdl_class'),
      endorsements: formData.get('endorsements'),
      hire_date: formData.get('hire_date') ? new Date(formData.get('hire_date') as string).getTime() / 1000 : null,
      status: formData.get('status'),
      current_vehicle_id: formData.get('current_vehicle_id') || null,
    }

    if (editingDriver) {
      updateMutation.mutate({ id: editingDriver.id, data })
    } else {
      createMutation.mutate(data)
    }
  }

  const handleEdit = (driver: any) => {
    setEditingDriver(driver)
    setIsDialogOpen(true)
  }

  const handleDelete = (id: string, name: string) => {
    if (confirm(`Delete driver "${name}"?`)) {
      deleteMutation.mutate(id)
    }
  }

  const isLicenseExpiring = (expiresTimestamp: number) => {
    if (!expiresTimestamp) return false
    const expiresDate = new Date(expiresTimestamp * 1000)
    const now = new Date()
    const daysUntilExpiry = Math.floor((expiresDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
    return daysUntilExpiry <= 30 && daysUntilExpiry >= 0
  }

  const isLicenseExpired = (expiresTimestamp: number) => {
    if (!expiresTimestamp) return false
    const expiresDate = new Date(expiresTimestamp * 1000)
    return expiresDate < new Date()
  }

  const formatDate = (timestamp: number) => {
    if (!timestamp) return '-'
    return new Date(timestamp * 1000).toLocaleDateString()
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Driver Management
          </CardTitle>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => setEditingDriver(null)} className="gap-2">
                <Plus className="h-4 w-4" />
                Add Driver
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{editingDriver ? 'Edit Driver' : 'Add New Driver'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Full Name *</Label>
                    <Input name="name" defaultValue={editingDriver?.name} required />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input name="email" type="email" defaultValue={editingDriver?.email} />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input name="phone" type="tel" defaultValue={editingDriver?.phone} />
                  </div>
                  <div>
                    <Label>License Number</Label>
                    <Input name="license_number" defaultValue={editingDriver?.license_number} />
                  </div>
                  <div>
                    <Label>License State</Label>
                    <Input name="license_state" maxLength={2} placeholder="CA" defaultValue={editingDriver?.license_state} />
                  </div>
                  <div>
                    <Label>License Expires</Label>
                    <Input
                      name="license_expires"
                      type="date"
                      defaultValue={editingDriver?.license_expires ? new Date(editingDriver.license_expires * 1000).toISOString().split('T')[0] : ''}
                    />
                  </div>
                  <div>
                    <Label>CDL Class</Label>
                    <Select name="cdl_class" defaultValue={editingDriver?.cdl_class || 'A'}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="A">Class A</SelectItem>
                        <SelectItem value="B">Class B</SelectItem>
                        <SelectItem value="C">Class C</SelectItem>
                        <SelectItem value="none">No CDL</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Endorsements</Label>
                    <Input name="endorsements" placeholder="H, N, T" defaultValue={editingDriver?.endorsements} />
                  </div>
                  <div>
                    <Label>Hire Date</Label>
                    <Input
                      name="hire_date"
                      type="date"
                      defaultValue={editingDriver?.hire_date ? new Date(editingDriver.hire_date * 1000).toISOString().split('T')[0] : ''}
                    />
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select name="status" defaultValue={editingDriver?.status || 'active'}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                        <SelectItem value="on_leave">On Leave</SelectItem>
                        <SelectItem value="suspended">Suspended</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Assigned Vehicle (Optional)</Label>
                    <Input name="current_vehicle_id" placeholder="veh_001" defaultValue={editingDriver?.current_vehicle_id} />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                    {editingDriver ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-8">Loading...</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>License</TableHead>
                <TableHead>CDL Class</TableHead>
                <TableHead>Vehicle</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.drivers?.map((driver) => (
                <TableRow key={driver.id}>
                  <TableCell className="font-medium">{driver.name}</TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {driver.email && <div>{driver.email}</div>}
                      {driver.phone && <div className="text-muted-foreground">{driver.phone}</div>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {driver.license_number ? (
                        <>
                          <div>{driver.license_number} ({driver.license_state})</div>
                          <div className="flex items-center gap-1 mt-1">
                            {isLicenseExpired(driver.license_expires) ? (
                              <Badge variant="destructive" className="text-xs">
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                Expired {formatDate(driver.license_expires)}
                              </Badge>
                            ) : isLicenseExpiring(driver.license_expires) ? (
                              <Badge variant="outline" className="text-xs border-yellow-500 text-yellow-600">
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                Expires {formatDate(driver.license_expires)}
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground text-xs">
                                Exp: {formatDate(driver.license_expires)}
                              </span>
                            )}
                          </div>
                        </>
                      ) : (
                        '-'
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="capitalize">{driver.cdl_class || '-'}</TableCell>
                  <TableCell className="text-xs">{driver.current_vehicle_id || '-'}</TableCell>
                  <TableCell>
                    <Badge variant={driver.status === 'active' ? 'default' : 'secondary'} className="capitalize">
                      {driver.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" onClick={() => handleEdit(driver)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(driver.id, driver.name)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  )
}
